import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { s3ObjectPublicUrl } from '../../../utils/s3PublicUrl';
import { BOOK_LANGUAGE_FORM_EN_ID } from './bookLanguageFormSupport';
import type { BookLanguageRow } from './singleTableListSupport';
import { settingsListApiError } from './singleTableListSupport';
import { translateText } from './translationApi';

export type CategoryDraft = {
  mode: 'create' | 'edit';
  id: string;
  name: Record<string, string>;
  /** Existing S3 key from API; used for preview when no new file is selected. */
  image_path: string | null;
  /** New local file to upload on save (replaces `image_path` after upload). */
  imageFile: File | null;
};

const CATEGORY_IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

type CategoryListFormProps = {
  draft: CategoryDraft;
  onChange: (next: CategoryDraft) => void;
  languages: BookLanguageRow[];
  languagesLoading: boolean;
  languagesError: string | null;
};

export function validateCategoryDraft(draft: CategoryDraft, displayLanguages: BookLanguageRow[]): string | null {
  if (draft.mode === 'edit' && !draft.id.trim()) return 'ID алга.';
  if (displayLanguages.length === 0) {
    return 'Хэлийн жагсаалт хоосон. Эхлээд «Хэл» хэсэгт номын хэл нэмнэ үү.';
  }
  for (const lang of displayLanguages) {
    if (!(draft.name[lang.id] ?? '').trim()) {
      return `${lang.name} (${lang.id}) нэр заавал бөглөнө.`;
    }
  }
  return null;
}

/** `name` field for `POST /category` and `PUT /category` bodies. */
export function categoryDraftToNamePayload(draft: CategoryDraft): Record<string, string> {
  const name: Record<string, string> = {};
  for (const [code, raw] of Object.entries(draft.name)) {
    const t = raw.trim();
    if (t) name[code] = t;
  }
  return name;
}

export default function CategoryListForm({
  draft,
  onChange,
  languages,
  languagesLoading,
  languagesError,
}: CategoryListFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [objectPreviewUrl, setObjectPreviewUrl] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);

  const enSource = (draft.name[BOOK_LANGUAGE_FORM_EN_ID] ?? '').trim();
  const hasEnField = languages.some((l) => l.id === BOOK_LANGUAGE_FORM_EN_ID);
  const otherLanguages = languages.filter((l) => l.id !== BOOK_LANGUAGE_FORM_EN_ID);
  const canTranslateFromEn = hasEnField && enSource.length > 0 && otherLanguages.length > 0;

  useEffect(() => {
    setTranslateError(null);
  }, [enSource]);

  useEffect(() => {
    if (!draft.imageFile) {
      setObjectPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(draft.imageFile);
    setObjectPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [draft.imageFile]);

  const setNameForLang = (langId: string, value: string) => {
    onChange({ ...draft, name: { ...draft.name, [langId]: value } });
  };

  const displayImageSrc = objectPreviewUrl ?? s3ObjectPublicUrl(draft.image_path);

  const handleTranslateFromEn = async () => {
    if (!canTranslateFromEn || translating) return;
    setTranslating(true);
    setTranslateError(null);
    const targets = otherLanguages.map((l) => (l.code?.trim() ? l.code.trim() : l.id));
    try {
      const results = await translateText(enSource, BOOK_LANGUAGE_FORM_EN_ID, targets);
      if (results.length !== targets.length) {
        setTranslateError('Орчуулга бүрэн ирсэнгүй. Дахин оролдоно уу.');
        return;
      }
      const nextName = { ...draft.name };
      otherLanguages.forEach((l, i) => {
        nextName[l.id] = results[i] ?? '';
      });
      onChange({ ...draft, name: nextName });
    } catch (e: unknown) {
      setTranslateError(settingsListApiError(e, 'Орчуулахад алдаа гарлаа.'));
    } finally {
      setTranslating(false);
    }
  };

  if (languagesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} aria-label="Ачааллаж байна" />
      </Box>
    );
  }

  if (languagesError) {
    return <Alert severity="error">{languagesError}</Alert>;
  }

  return (
    <Stack spacing={2}>
      {draft.mode === 'edit' && (
        <TextField label="ID" name="id" value={draft.id} disabled autoComplete="off" fullWidth margin="none" />
      )}
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Зургийг S3 дээрх <Box component="span" sx={{ fontFamily: 'monospace' }}>category/</Box> хавтас руу оруулна.
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept={CATEGORY_IMAGE_ACCEPT}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            e.target.value = '';
            onChange({ ...draft, imageFile: f });
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateOutlined />}
            onClick={() => fileInputRef.current?.click()}
          >
            Зураг сонгох
          </Button>
          {draft.imageFile !== null && (
            <Button type="button" size="small" onClick={() => onChange({ ...draft, imageFile: null })}>
              Сонгосон зургийг цуцлах
            </Button>
          )}
        </Stack>
        {displayImageSrc && (
          <Box
            sx={{
              mt: 0.5,
              borderRadius: 1,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              maxWidth: 280,
              bgcolor: 'action.hover',
            }}
          >
            <Box
              component="img"
              src={displayImageSrc}
              alt=""
              sx={{ display: 'block', width: '100%', maxHeight: 160, objectFit: 'contain' }}
            />
          </Box>
        )}
      </Stack>
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Номын санд бүртгэлтэй бүх хэл дээр нэр заавал оруулна.
        </Typography>
        {canTranslateFromEn && (
          <Box>
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={translating ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />}
              disabled={translating}
              onClick={() => void handleTranslateFromEn()}
            >
              Англи текстийг бусад хэл рүү орчуулах
            </Button>
          </Box>
        )}
        {translateError && (
          <Alert severity="error" onClose={() => setTranslateError(null)}>
            {translateError}
          </Alert>
        )}
      </Stack>
      {languages.map((lang) => {
        const val = draft.name[lang.id] ?? '';
        return (
          <TextField
            key={lang.id}
            label={`${lang.name} (${lang.id})`}
            value={val}
            onChange={(e) => setNameForLang(lang.id, e.target.value)}
            required
            fullWidth
            autoComplete="off"
            margin="dense"
            inputProps={{ 'aria-required': true }}
          />
        );
      })}
    </Stack>
  );
}
